import React, { useState, useEffect } from 'react';
import { authApi, User } from '../lib/auth';
import { AuthContext } from '../hooks/useAuth';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      // If we already have a user, skip loading
      if (user) {
        setLoading(false);
        setIsInitialized(true);
        return;
      }

      // Check if we have a token
      const hasToken = authApi.isAuthenticated();
      
      if (!hasToken) {
        setUser(null);
        setLoading(false);
        setIsInitialized(true);
        return;
      }

      // We have a token but no user - fetch user data
      try {
        const { user: fetchedUser } = await authApi.getMe();
        setUser(fetchedUser);
      } catch (error) {
        // Clear auth state for 401 (unauthorized) or 403 (forbidden)
        // Both indicate session expired or authentication issues
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          // Clear tokens and user state
          // Don't redirect here - let the API interceptor handle redirects for actual API calls
          authApi.logout();
          setUser(null);
        } else {
          // For other errors, still clear state to be safe
          authApi.logout();
          setUser(null);
        }
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    // Only load if not already initialized
    if (!isInitialized) {
      loadUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Only depend on isInitialized to prevent loops

  // Listen for auth expiration events from API interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      // Clear user state when auth expires
      setUser(null);
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      // Set user immediately from login response to avoid race condition with getMe()
      setUser(response.user);
      setLoading(false);
      setIsInitialized(true);
      
      // Force a re-render to ensure state is propagated
      // This helps ensure ProtectedRoute sees the updated state
      await new Promise(resolve => setTimeout(resolve, 0));
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const response = await authApi.register({ email, password, firstName, lastName });
    setUser(response.user);
    setLoading(false);
    setIsInitialized(true);
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
    setIsInitialized(false);
    // Clear any auth expiration flags
    sessionStorage.removeItem('auth-expired');
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}


